import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { auth } from "../../../firebase/config";
import Config from "../../../Config";
import { useAuth } from "../../../utils/AuthContext";
import { useSendEmail } from "../../../hooks/useSendEmail";
import { verificationCodeEmail } from "../../../emails";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";

const CODE_LENGTH = 6;

const VerifyCode: React.FC = () => {
  const [email, setEmail] = useState("");
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const { completeSignup } = useAuth();
  const { sendEmail } = useSendEmail();

  useEffect(() => {
    const pendingEmail = sessionStorage.getItem("pendingVerificationEmail");
    if (!pendingEmail) {
      navigate("/signup");
      return;
    }
    setEmail(pendingEmail);
    inputRefs.current[0]?.focus();
  }, [navigate]);

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted.charAt(i);
    }
    setDigits(next);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length < CODE_LENGTH) {
      setError("Please enter the full 6-digit code.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        setError("Session expired. Please sign up again.");
        navigate("/signup");
        return;
      }

      const pendingPlan = localStorage.getItem("pendingPlan") || "free";
      const response = await fetch(`${Config.API_URL}/api/auth/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          code,
          firebase_uid: user.uid,
          account_type: pendingPlan,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || "Invalid or expired code. Please try again.");
        setLoading(false);
        return;
      }

      const data = await response.json();
      const backendToken = data.access_token;

      // Handle pending org setup/accept tokens
      const pendingOrgSetupToken = sessionStorage.getItem(
        "pendingOrgSetupToken",
      );
      const pendingOrgAcceptToken = sessionStorage.getItem(
        "pendingOrgAcceptToken",
      );

      if (pendingOrgSetupToken || pendingOrgAcceptToken) {
        try {
          const firebaseToken = await user.getIdToken(true);

          if (pendingOrgSetupToken) {
            await fetch(`${Config.API_URL}/api/organization/setup`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${backendToken}`,
                "X-Firebase-Authorization": `Bearer ${firebaseToken}`,
              },
              body: JSON.stringify({ token: pendingOrgSetupToken }),
            });
            sessionStorage.removeItem("pendingOrgSetupToken");
          }

          if (pendingOrgAcceptToken) {
            await fetch(
              `${Config.API_URL}/api/organization/accept-invitation`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${backendToken}`,
                  "X-Firebase-Authorization": `Bearer ${firebaseToken}`,
                },
                body: JSON.stringify({ token: pendingOrgAcceptToken }),
              },
            );
            sessionStorage.removeItem("pendingOrgAcceptToken");
          }
        } catch (err) {
          console.error("Failed to complete organization setup/accept:", err);
        }
      }

      sessionStorage.removeItem("pendingVerificationEmail");
      sessionStorage.removeItem("pendingVerificationName");

      if (localStorage.getItem("pendingPlan") === "individual") {
        localStorage.removeItem("pendingPlan");
        localStorage.setItem("payment_pending", "true");
      }

      await completeSignup(backendToken);

      if (localStorage.getItem("payment_pending") === "true") {
        navigate("/card-details");
      } else {
        navigate("/home/dashboard");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMessage("");
    setError("");
    try {
      const user = auth.currentUser;
      if (!user) {
        setResendMessage("Session expired. Please sign up again.");
        return;
      }
      const response = await fetch(
        `${Config.API_URL}/api/auth/send-verification-code`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, firebase_uid: user.uid }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        await sendEmail({ to: email, template: verificationCodeEmail({ code: data.code }) });
        setResendMessage("A new code has been sent to your email.");
        setDigits(Array(CODE_LENGTH).fill(""));
        inputRefs.current[0]?.focus();
      } else {
        setResendMessage("Failed to resend code. Please try again.");
      }
    } catch {
      setResendMessage("Failed to resend code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-gray-50">
      <div className="w-full max-w-md px-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div className="mb-6">
            <Mail className="mx-auto text-[#1d365d]" size={50} />
          </div>

          <h2 className="text-3xl font-semibold mb-2">Check your email</h2>

          <p className="text-gray-600 mb-1">We sent a 6-digit code to:</p>
          <p className="text-lg font-medium text-[#1d365d] mb-6">{email}</p>

          <form onSubmit={handleSubmit}>
            <div className="flex justify-center gap-2 mb-6">
              {digits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputRefs.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={handlePaste}
                  disabled={loading}
                  className="w-11 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#1d365d] disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              ))}
            </div>

            {error && (
              <div
                className="mb-4 px-4 py-3 rounded bg-red-100 border border-red-400 text-red-700 text-sm"
                role="alert"
              >
                {error}
              </div>
            )}

            {resendMessage && (
              <div
                className={`mb-4 px-4 py-3 rounded text-sm ${
                  resendMessage.includes("new code")
                    ? "bg-green-100 border border-green-400 text-green-700"
                    : "bg-red-100 border border-red-400 text-red-700"
                }`}
                role="alert"
              >
                {resendMessage}
              </div>
            )}

            <PrimaryButton type="submit" disabled={loading} className="w-full">
              {loading ? "Verifying..." : "Verify"}
            </PrimaryButton>
          </form>

          <button
            onClick={handleResend}
            disabled={resending || loading}
            className="mt-4 text-sm text-[#1d365d] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resending ? "Sending..." : "Resend code"}
          </button>

          <p className="mt-4 text-xs text-gray-400">
            Code expires in 10 minutes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyCode;
